import React, { useEffect, useState } from 'react';

const CLAVE_CERRADO = 'pf_instalar_banner_cerrado';
const CLAVE_INSTALADA = 'pf_app_instalada';
const DIAS_ESPERA_TRAS_CERRAR = 14;
const DURACION_SALIDA_MS = 240;

function yaInstalada() {
  const enModoApp =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true; // iOS
  return enModoApp || localStorage.getItem(CLAVE_INSTALADA) === '1';
}

function fueCerradoRecientemente() {
  const guardado = localStorage.getItem(CLAVE_CERRADO);
  if (!guardado) return false;
  const dias = (Date.now() - Number(guardado)) / (1000 * 60 * 60 * 24);
  return dias < DIAS_ESPERA_TRAS_CERRAR;
}

function esIOS() {
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
}

function IconoCerrar(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function IconoInstalar(props) {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 3v12" />
      <path d="M7 10l5 5 5-5" />
      <path d="M4 19h16" />
    </svg>
  );
}

function IconoCompartir(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 15V4" />
      <path d="M8 8l4-4 4 4" />
      <rect x="5" y="11" width="14" height="9" rx="2" />
    </svg>
  );
}

function IconoAgregar(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="4" y="4" width="16" height="16" rx="4" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}

export default function InstalarAppBanner() {
  const [visible, setVisible] = useState(false);
  const [saliendo, setSaliendo] = useState(false);
  const [plataforma, setPlataforma] = useState(null); // 'android' | 'ios'
  const [promptEvent, setPromptEvent] = useState(null);
  const [instalando, setInstalando] = useState(false);

  useEffect(() => {
    if (yaInstalada() || fueCerradoRecientemente()) return;

    // Android/Chrome: el navegador avisa cuando la app cumple los requisitos para instalarse.
    function alDetectarInstalable(evento) {
      evento.preventDefault();
      setPromptEvent(evento);
      setPlataforma('android');
      setVisible(true);
    }
    window.addEventListener('beforeinstallprompt', alDetectarInstalable);

    // iOS no tiene ese aviso (Apple no lo permite) — mostramos instrucciones manuales.
    let temporizador;
    if (esIOS()) {
      temporizador = setTimeout(() => {
        if (!yaInstalada() && !fueCerradoRecientemente()) {
          setPlataforma('ios');
          setVisible(true);
        }
      }, 1500);
    }

    function alInstalar() {
      localStorage.setItem(CLAVE_INSTALADA, '1');
      cerrarConAnimacion();
    }
    window.addEventListener('appinstalled', alInstalar);

    return () => {
      window.removeEventListener('beforeinstallprompt', alDetectarInstalable);
      window.removeEventListener('appinstalled', alInstalar);
      clearTimeout(temporizador);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cerrarConAnimacion(alTerminar) {
    setSaliendo(true);
    setTimeout(() => {
      setVisible(false);
      setSaliendo(false);
      alTerminar?.();
    }, DURACION_SALIDA_MS);
  }

  function cerrar() {
    cerrarConAnimacion(() => localStorage.setItem(CLAVE_CERRADO, String(Date.now())));
  }

  async function instalarAndroid() {
    if (!promptEvent || instalando) return;
    setInstalando(true);
    promptEvent.prompt();
    try {
      const { outcome } = await promptEvent.userChoice;
      localStorage.setItem(outcome === 'accepted' ? CLAVE_INSTALADA : CLAVE_CERRADO, outcome === 'accepted' ? '1' : String(Date.now()));
    } finally {
      setPromptEvent(null);
      setInstalando(false);
      cerrarConAnimacion();
    }
  }

  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes pfBannerEntrar {
          from { transform: translateY(calc(100% + 32px)); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        .pf-instalar-banner {
          animation: pfBannerEntrar 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .pf-instalar-banner.pf-saliendo {
          animation: pfBannerEntrar 0.22s cubic-bezier(0.4, 0, 1, 1) reverse both;
        }
        @media (prefers-reduced-motion: reduce) {
          .pf-instalar-banner, .pf-instalar-banner.pf-saliendo { animation: none; }
        }
        .pf-instalar-btn { transition: background-color 0.15s ease, transform 0.1s ease; }
        .pf-instalar-btn:hover:not(:disabled) { background-color: #3b74f0; }
        .pf-instalar-btn:active:not(:disabled) { transform: scale(0.97); }
        .pf-instalar-cerrar { transition: background-color 0.15s ease, opacity 0.15s ease; }
        .pf-instalar-cerrar:hover { background-color: rgba(255,255,255,0.12); opacity: 1; }
        .pf-instalar-banner *:focus-visible {
          outline: 2px solid #5b8def;
          outline-offset: 2px;
          border-radius: 6px;
        }
        @media (min-width: 640px) {
          .pf-instalar-banner {
            left: auto !important;
            right: 20px !important;
            bottom: 20px !important;
            width: 380px !important;
            border-radius: 18px !important;
            border-bottom: 1px solid rgba(255,255,255,0.08) !important;
            box-shadow: 0 24px 48px rgba(2, 8, 20, 0.4), 0 4px 12px rgba(2, 8, 20, 0.25) !important;
          }
        }
      `}</style>
      <div
        role="group"
        aria-label="Instalar Playfesor en este dispositivo"
        className={`pf-instalar-banner${saliendo ? ' pf-saliendo' : ''}`}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          background: 'linear-gradient(180deg, #0b1a30 0%, #07111f 100%)',
          color: '#fff',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          border: '1px solid rgba(255,255,255,0.08)',
          borderBottom: 'none',
          padding: '16px 16px calc(16px + env(safe-area-inset-bottom, 0px))',
          boxShadow: '0 -14px 32px rgba(2, 8, 20, 0.45), 0 -2px 8px rgba(2,8,20,0.3)',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div
            style={{
              width: 44, height: 44, borderRadius: 12, flexShrink: 0,
              background: 'rgba(37, 99, 235, 0.15)',
              border: '1px solid rgba(37, 99, 235, 0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <img src="/logo192.png" alt="" style={{ width: 28, height: 28, borderRadius: 6 }} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: '-0.01em' }}>Instala Playfesor</div>
            <div style={{ fontSize: 13, color: 'rgba(219, 230, 255, 0.72)', marginTop: 2, lineHeight: 1.4 }}>
              {plataforma === 'android'
                ? 'Acceso directo desde tu pantalla de inicio, sin buscar en el navegador.'
                : 'Agrégala a tu pantalla de inicio para entrar en un toque.'}
            </div>
          </div>

          <button
            onClick={cerrar}
            aria-label="Cerrar aviso de instalación"
            className="pf-instalar-cerrar"
            style={{
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              background: 'transparent', border: 'none', color: '#fff', opacity: 0.65,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <IconoCerrar />
          </button>
        </div>

        {plataforma === 'android' ? (
          <button
            onClick={instalarAndroid}
            disabled={instalando}
            className="pf-instalar-btn"
            style={{
              width: '100%', marginTop: 14, background: '#2563eb', color: '#fff', border: 'none',
              borderRadius: 11, padding: '12px 16px', fontWeight: 600, fontSize: 14.5,
              cursor: instalando ? 'default' : 'pointer', opacity: instalando ? 0.75 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <IconoInstalar />
            {instalando ? 'Instalando…' : 'Instalar app'}
          </button>
        ) : (
          <div
            style={{
              marginTop: 14, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 10,
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 11, padding: '10px 12px', fontSize: 13, color: 'rgba(255,255,255,0.85)',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconoCompartir style={{ color: '#5b8def' }} />
              <strong style={{ fontWeight: 600, color: '#fff' }}>Compartir</strong>
            </span>
            <span style={{ opacity: 0.4 }} aria-hidden="true">→</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconoAgregar style={{ color: '#5b8def', flexShrink: 0 }} />
              <strong style={{ fontWeight: 600, color: '#fff' }}>Agregar a inicio</strong>
            </span>
          </div>
        )}
      </div>
    </>
  );
}
